import { useEffect } from "react";

// "2026-04-16" + "2026-04-20" → "16th to 20th April 2026"
function ordinal(n) {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}
function formatRange(startISO, endISO) {
  if (!startISO) return "Schedule TBA";
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const s = new Date(startISO + "T00:00:00");
  const e = endISO ? new Date(endISO + "T00:00:00") : null;
  const sd = s.getDate(), sm = s.getMonth(), sy = s.getFullYear();
  if (!e) return `${ordinal(sd)} ${months[sm]} ${sy}`;
  const ed = e.getDate(), em = e.getMonth(), ey = e.getFullYear();
  if (sm === em && sy === ey) return `${ordinal(sd)} to ${ordinal(ed)} ${months[sm]} ${sy}`;
  if (sy === ey) return `${ordinal(sd)} ${months[sm]} to ${ordinal(ed)} ${months[em]} ${sy}`;
  return `${ordinal(sd)} ${months[sm]} ${sy} to ${ordinal(ed)} ${months[em]} ${ey}`;
}

/**
 * EventDetailsModal (multi-venue aware).
 * Props:
 *  - city: exhibition object (may include venues[])
 *  - venues: normalised venue list (>=1 means multi-venue selector shown)
 *  - selectedVenue: currently selected venue object (or null)
 *  - onSelectVenue(venue): called when user picks a venue pill
 *  - onClose(): close modal
 *  - onRegister(): main CTA (Register Now / Notify Me)
 *
 * When venues.length > 0 (multi-venue mode) — the details section reflects the
 * SELECTED venue's dates/timings/venue/address/status. If no venue selected, the
 * details area shows a prompt and the CTA is disabled.
 * When venues is empty — falls back to top-level city fields (single-venue mode).
 */
export default function EventDetailsModal({
  city,
  venues = [],
  selectedVenue = null,
  onSelectVenue,
  onClose,
  onRegister,
}) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  if (!city) return null;
  const multiVenue = venues && venues.length > 0;

  // The "active" data source — a specific venue when selected, else fall back
  // to the exhibition's top-level fields (single-venue mode).
  const active = multiVenue
    ? (selectedVenue || null)
    : {
        start_date: city.start_date,
        end_date: city.end_date,
        timings: city.timings,
        venue_name: city.venue,
        address: city.address,
        status: city.status,
      };

  const dates = active ? formatRange(active.start_date, active.end_date) : "";
  const mapsQuery = active ? encodeURIComponent([active.venue_name, active.address].filter(Boolean).join(", ")) : "";
  const mapsUrl = mapsQuery ? `https://www.google.com/maps/search/?api=1&query=${mapsQuery}` : null;

  const activeStatus = active?.status || (multiVenue ? "soon" : city.status);
  const isLive = activeStatus === "live";
  const canProceed = multiVenue ? !!selectedVenue : true;

  const overlayClick = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div className="rl-modal-overlay" data-testid="event-details-modal" onClick={overlayClick}>
      <div className="rl-modal rl-event-modal" role="dialog" aria-modal="true">
        <button className="rl-modal-close" data-testid="event-details-close" onClick={onClose} aria-label="Close">✕</button>

        <div className="rl-modal-head">
          {multiVenue ? (
            <span className="rl-badge rl-badge-multi" style={{ alignSelf: "flex-start" }}>
              Multiple Venues
            </span>
          ) : (
            isLive ? (
              <span className="rl-badge rl-badge-live" style={{ alignSelf: "flex-start" }}>
                <span className="dot" /> Live Exhibition
              </span>
            ) : (
              <span className="rl-badge rl-badge-soon" style={{ alignSelf: "flex-start" }}>
                Coming Soon
              </span>
            )
          )}
          <h3 className="rl-modal-title" style={{ marginTop: 10 }}>
            Rudralife in <span className="gold">{city.name}</span>
          </h3>
          <p className="rl-modal-sub">
            An exclusive opportunity to experience authentic, lab-certified Rudraksha under expert guidance.
          </p>

          {/* Venue selector — only when the exhibition has 2+ venues */}
          {multiVenue && (
            <div className="rl-venue-pills" data-testid="venue-pills" role="tablist" aria-label="Select a venue">
              {venues.map((v) => {
                const isActive = selectedVenue && selectedVenue.slug === v.slug;
                return (
                  <button
                    key={v.slug}
                    type="button"
                    className={`rl-venue-pill${isActive ? " active" : ""}`}
                    onClick={() => onSelectVenue && onSelectVenue(v)}
                    data-testid={`venue-pill-${v.slug}`}
                    role="tab"
                    aria-selected={isActive}
                  >
                    {v.label || v.slug}
                    <span className={`rl-venue-pill-badge ${v.status === "live" ? "live" : "soon"}`}>
                      {v.status === "live" ? "Live" : "Soon"}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="rl-event-info" data-testid="event-info">
          {multiVenue && !selectedVenue ? (
            <div className="rl-venue-prompt" data-testid="venue-prompt">
              Please select a venue above to see the schedule &amp; address.
            </div>
          ) : (
            <>
              <div className="rl-event-row">
                <div className="rl-event-icon" aria-hidden="true">📅</div>
                <div>
                  <div className="rl-event-label">Dates</div>
                  <div className="rl-event-value" data-testid="event-dates">{dates}</div>
                </div>
              </div>

              <div className="rl-event-row">
                <div className="rl-event-icon" aria-hidden="true">🕒</div>
                <div>
                  <div className="rl-event-label">Timings</div>
                  <div className="rl-event-value" data-testid="event-timings">
                    {active?.timings || "10:00 am to 8:00 pm (Sunday Open)"}
                  </div>
                </div>
              </div>

              {(active?.venue_name || active?.address) && (
                <div className="rl-event-row">
                  <div className="rl-event-icon" aria-hidden="true">📍</div>
                  <div style={{ minWidth: 0 }}>
                    <div className="rl-event-label">Venue</div>
                    {active.venue_name && <div className="rl-event-value" data-testid="event-venue">{active.venue_name}</div>}
                    {active.address && (
                      mapsUrl ? (
                        <a
                          href={mapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rl-event-maps-link"
                          data-testid="event-maps-link"
                          title="Open in Google Maps"
                        >
                          {active.address}
                        </a>
                      ) : (
                        <div className="rl-event-value">{active.address}</div>
                      )
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="rl-modal-foot rl-event-foot">
          <button className="rl-btn-text" onClick={onClose} data-testid="event-details-cancel">Maybe Later</button>
          <button
            className="rl-btn rl-btn-primary"
            data-testid="event-register-now-btn"
            onClick={() => canProceed && onRegister && onRegister()}
            disabled={!canProceed}
            aria-disabled={!canProceed}
            title={!canProceed ? "Please select a venue first" : ""}
          >
            {isLive ? "Register Now →" : "Notify Me →"}
          </button>
        </div>
      </div>
    </div>
  );
}
